// General
import React, { useContext, useState, useMemo, useEffect } from "react";

// Core / Table
import TablePage from "../core/TablePage";

// Contexts
import { ServiceContext } from "../data/services/ServiceContext";
import { ActionContext } from "../data/action/ActionContext";
import { SubActionContext } from "../data/action/SubActionContext";
import { PersonelContext } from "../data/personel/PersonelContext";

// UI / MUI
import { TextField, Autocomplete, InputAdornment } from "@mui/material";

// Layout / Bootstrap
import { Row, Col } from "react-bootstrap";

const ServiceActionPage = ({ serviceId }) => {
  // Contextleri kullanarak gerekli verileri çek
  const { services, updateService } = useContext(ServiceContext);
  const { actions } = useContext(ActionContext);
  const { subActions } = useContext(SubActionContext);
  const { personelList } = useContext(PersonelContext);

  // İlgili servisi seç
  const service = services.find((s) => s.id === serviceId);

  // Atanmış aksiyonu bul
  const assignedAction = actions.find((a) => a.id === service?.actionId);

  // Mevcut aksiyon için alt aksiyonları filtrele
  const availableSubActions = useMemo(() => {
    if (!assignedAction) return [];
    return subActions.filter((sa) => assignedAction.subActions.includes(sa.id));
  }, [assignedAction, subActions]);

  // Form state
  const [selectedSubAction, setSelectedSubAction] = useState(null);
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [personnel, setPersonnel] = useState(null);
  const [paymentDate, setPaymentDate] = useState("");
  const [workDone, setWorkDone] = useState("");
  const [offer, setOffer] = useState("");
  const [callTime, setCallTime] = useState("");
  const [price, setPrice] = useState("");

  useEffect(() => {
    if (!service) return;

    const currentSub = service.subActionId
      ? subActions.find((sa) => sa.id === service.subActionId)
      : null;
    setSelectedSubAction(currentSub || null);

    const extra = service.extraData || {};
    setNote(extra.note || "");
    setAmount(extra.amount || "");
    setPersonnel(extra.personnel || null);
    setPaymentDate(extra.paymentDate || "");
    setWorkDone(extra.workDone || "");
    setOffer(extra.offer || "");
    setCallTime(extra.callTime || "");
    setPrice(extra.price || "");
  }, [service, subActions]);

  const callTimeOptions = [
    "09:00-10:00",
    "10:00-11:00",
    "11:00-12:00",
    "12:00-13:00",
    "13:00-14:00",
    "14:00-15:00",
    "15:00-16:00",
  ];

  const renderDynamicFields = () => {
    if (!selectedSubAction) return null;

    switch (selectedSubAction.name) {
      case "Haber Verecek":
      case "Müşteri İptal Etti":
      case "Teyid Araması":
      case "Cihaz Tamir Edilemiyor":
      case "Şikayetçi":
      case "Yeniden Teknisyen Yönlendir":
      case "Servis Sonlandırıldı":
      case "Parçası Atölyeye Alındı":
      case "Cihaz Teslim Edildi":
      case "Fiyatta Anlaşılamadı":
        return (
          <TextField
            label="Açıklama"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        );

      case "Tahsilata Gönder":
        return (
          <>
            <Row>
              <Col>
                <TextField
                  label="Ödeme Tarihi"
                  type="date"
                  size="small"
                  fullWidth
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Col>
              <Col>
                <TextField
                  label="Tutar"
                  type="number"
                  size="small"
                  fullWidth
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">₺</InputAdornment>,
                  }}
                />
              </Col>
            </Row>
            <Autocomplete
              options={personelList}
              value={personnel}
              onChange={(e, newVal) => setPersonnel(newVal)}
              getOptionLabel={(option) => option?.fullName || ""}
              renderInput={(params) => (
                <TextField {...params} label="Personel" size="small" className="mt-2" />
              )}
              noOptionsText="Personel bulunamadı"
            />
          </>
        );

      case "Yerinde Bakım Yapıldı":
        return (
          <>
            <TextField
              label="Yapılan İşlemler"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={workDone}
              onChange={(e) => setWorkDone(e.target.value)}
            />
            <TextField
              label="Verilen Teklif"
              size="small"
              fullWidth
              className="mt-2"
              value={offer}
              onChange={(e) => setOffer(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">₺</InputAdornment>,
              }}
            />
            <TextField
              label="Açıklama"
              size="small"
              fullWidth
              multiline
              rows={2}
              className="mt-2"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        );

      case "Müşteriye Ulaşılamadı":
        return (
          <>
            <Autocomplete
              options={callTimeOptions}
              value={callTime || null}
              onChange={(e, newVal) => setCallTime(newVal)}
              renderInput={(params) => (
                <TextField {...params} label="Arama Saati Aralığı" size="small" />
              )}
              className="mb-2"
            />
            <TextField
              label="Açıklama"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        );

      case "Atölyeye Alındı":
      case "Parça Gidecek":
        return (
          <>
            <TextField
              label="Müşteriye Verilen Fiyat"
              type="number"
              size="small"
              fullWidth
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">₺</InputAdornment>,
              }}
            />
            <TextField
              label="Açıklama"
              size="small"
              fullWidth
              multiline
              rows={2}
              className="mt-2"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        );

      default:
        return null;
    }
  };

  const handleSave = () => {
    if (!service) return;

    updateService(service.id, {
      subActionId: selectedSubAction ? selectedSubAction.id : undefined,
      extraData: {
        ...(service.extraData || {}),
        note,
        amount,
        personnel,
        paymentDate,
        workDone,
        offer,
        callTime,
        price,
      },
    });
  };

  const columns = [
    { field: "id", header: "ID", hideInFilter: true },
    {
      field: "actionName",
      header: "Servis Durumu",
      body: (row) => {
        const assigned = actions.find((a) => a.id === row.actionId);
        return assigned ? assigned.name : "Beklemede";
      },
    },
    {
      field: "subActionName",
      header: "Yapılacak İşlem",
      body: (row) => {
        const sub = subActions.find((sa) => sa.id === row.subActionId);
        return sub ? sub.name : "-";
      },
    },
    {
      field: "details",
      header: "Detaylar",
      hideInFilter: true,
      body: (row) => {
        const extra = row.extraData || {};
        const details = [];
        if (extra.note) details.push(extra.note);
        if (extra.amount) details.push(`${extra.amount} ₺`);
        if (extra.personnel?.fullName) details.push(extra.personnel.fullName);
        return details.length > 0 ? (
          <div className="flex flex-col gap-1">
            {details.map((d, i) => (
              <div key={i}>{d}</div>
            ))}
          </div>
        ) : (
          "-"
        );
      },
    },
  ];

  return (
    <div>
      <TablePage
        title="Servis Aksiyonları"
        subTitle="Servise aksiyon ekleyebilir, düzenleyebilir veya silebilirsiniz."
        columns={columns}
        data={services.filter((s) => s.id === serviceId)}
        defaultRows={1}
        modalFilterStyle
        showHeader={false}
        showPrint={false}
        showDownload={false}
        extraHeaderContent={
          <div className="flex flex-col gap-3">
            <Autocomplete
              options={availableSubActions}
              value={selectedSubAction || null}
              onChange={(e, newVal) => setSelectedSubAction(newVal)}
              getOptionLabel={(option) => option?.name || ""}
              renderInput={(params) => (
                <TextField {...params} label="Yapılacak işlem seçin" size="small" />
              )}
              style={{ width: 250 }}
              disablePortal
              autoHighlight
            />
          </div>
        }
        extraFormHeader={
          selectedSubAction && (
            <div className="flex justify-center mt-2">
              <div className="flex flex-col gap-3 p-3 border rounded bg-white w-full md:w-1/2 max-w-[1000px]">
                {renderDynamicFields()}

                <div className="flex justify-end gap-2 mt-2">
                  <button
                    className="btn btn-secondary w-full md:w-auto"
                    onClick={() => {
                      // Alanları resetle
                      setNote("");
                      setAmount("");
                      setPersonnel(null);
                      setPaymentDate("");
                      setWorkDone("");
                      setOffer("");
                      setCallTime("");
                      setPrice("");
                      setSelectedSubAction(null);
                    }}
                  >
                    İptal
                  </button>

                  <button
                    className="btn btn-primary w-full md:w-auto"
                    onClick={handleSave}
                  >
                    Kaydet
                  </button>
                </div>
              </div>
            </div>
          )
        }

      />
    </div>
  );
};

export default ServiceActionPage;
